export const apiEndpoint =
  `${process.env.VUE_APP_API_ENDPOINT}` || 'http://localhost:4000'

export const appStoreUrl = process.env.VUE_APP_IOS_STORE_URL
export const googlePlayUrl = process.env.VUE_APP_ANDROID_STORE_URL

export const algorithmCodeGlobals = `
declare const api: API;
declare const userJob: UserJob;

type API = {
  /**
   * Paycheck related API methods
   */
  paycheck: {
    /**
     * Adds an absence section to the paycheck of a given month
     * @param userJobId ID of current UserJob
     * @param month a string representation of the desired month (e.g. "2020-08")
     * @param absenceSection The absence object
     */
    addAbsenceSection (userJobId: string, month: string, absenceSection: AbsenceSection): Promise<void>;
    /**
     * Adds an deduction to the paycheck of a given month
     * @param userJobId ID of current UserJob
     * @param month a string representation of the desired month (e.g. "2020-08")
     * @param deduction The deduction object
     */
    addDeduction (userJobId: string, month: string, deduction: Deduction): Promise<void>;
    /**
     * Adds "extra data" to the paycheck of a given month
     * @param userJobId ID of current UserJob
     * @param month a string representation of the desired month (e.g. "2020-08")
     * @param extraData The extra data object
     */
    addExtraDataRecord (userJobId: string, month: string, extraData: ExtraDataRecord): Promise<void>;
    /**
     * Adds a payment to the paycheck of a given month
     * @param userJobId ID of current UserJob
     * @param month a string representation of the desired month (e.g. "2020-08")
     * @param payment The payment object
     */
    addPayment (userJobId: string, month: string, payment: Payment): Promise<void>;
  };
  /**
   * Shift related API methods
   */
  shift: {
    /**
     * Appends an existing hours group by its name, or create a new one if the name doesn't exist in the given shift
     * @param shiftId ID of desired shift
     * @param name name of the hours group (e.g. שכר בסיס)
     * @param hoursRecord the object to append to the given group
     */
    appendHoursGroup (shiftId: string, name: string, hoursRecord: ShiftHoursRecord): Promise<void>

    /**
     * Saves info to a specific shift (e.g. "You have the right to [...]")
     * @param shiftId ID of desired shift
     * @param info info to add
     */
    addInfo(shiftId: string, info: string): Promise<void>;

    /**
     * Sets a ShiftHoursGroup to an existing shift by name
     * @param shiftId ID of desired shift
     * @param name name of hours group to set
     * @param hoursRecord hoursRecord object to set
     */
    setHoursGroup (shiftId: string, name: string, hoursRecord: ShiftHoursRecord): Promise<void>
  };
}

type AbsenceSection {
  createdAt: string;
  id: string;
  addition: number;
  currentAmount: number;
  name: string;
  previousAmount: number;
  usage: number;
}

type Deduction = {
  id: string;
  createdAt: string;
  amount: number;
  title: string;
}

type ExtraDataRecord = {
  createdAt: string;
  id: string;
  text: string;
  value: number;
}

type Payment = {
  id: string;
  createdAt: string;
  title: string;
  totalPay: number;
  unitAmount?: number;
  unitPrice?: number;
}

type Paycheck = {
  id: string;
  createdAt: string;
  absences: AbsenceSection[];
  deductions: Deduction[];
  extraData: ExtraDataRecord[];
  month: string;
  payments: Payment[];
}

type UserJob = {
  id: string;
  createdAt: string;
  dailyTravelRefund: number;
  currentSalary: Salary;
  currentShift?: Shift;
  job: Job;
  paychecks: Paycheck[];
  shifts: Shift[];
  start: string;
  user: User;
}

type Job = {
  id: string
  createdAt: string
  address: string
  branch: Branch
  isArchived: boolean
  isVerified: boolean
  name: string
}

type Branch = {
  id: string
  createdAt: string
  name: string
  image: string
  isArchived: boolean
}

type User = {
  id: string;
  createdAt: string
  address?: string;
  birthday?: Date;
  email: string;
  firstName: string;
  image?: string;
  isArchived: boolean;
  isConfirmed: boolean;
  lastName: string;
  phone?: string;
  password: string;
}

interface EndJobForm {
  id: string;
  createdAt: string;
  didGetHearingProtocol: boolean;
  didGiveNotice: boolean;
  didHearingOccur: boolean;
  didWorkAfterNotice: boolean;
  endJobDate: string;
  endJobReason: "RESIGNATION" | "TERMINATION" | "UNKNOWN";
  notes: string;
  noticeDurationInDays: number;
  resignationReason: "HEALTH" | "WORSENING_TERMS" | "RELOCATION" | "BEGINNING_OF_SERVICE" | "OTHER";
}

interface Salary {
  id: string;
  createdAt: string
  end?: string;
  hourlyRate: number;
  start: string;
  type: SalaryType;
}

type SalaryType = "HOURLY" | "MINIMUM" | "TIPPED";

type Shift = {
  id: string;
  createdAt: string;
  bossTips?: number;
  duration: number;
  hourGroups: ShiftHoursGroup[];
  isEdited: boolean;
  moreInfo: string[];
  notes?: string;
  start: Date;
  totalTips?: number;
}

type ShiftHoursGroup = {
  id: string;
  name: string;
  records: ShiftHoursRecord[];
}

type ShiftHoursRecord = {
  id: string;
  createdAt: string;
  amount: number;
  payment: number;
  unitPrice: number;
}`
